import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import RegionBar from "@/components/RegionBar";
import { Clock, ShoppingBag, ArrowRight } from "lucide-react";

export default function CheckoutPage() {
  const { region, cart, subtotal, fmt, linePrice } = useShop();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/shop/settings").then(({ data }) => setSettings(data)).catch(() => setSettings({}));
  }, []);

  const orderingEnabled = settings && (region.market === "ethiopia" ? settings.ordering_enabled_eth : settings.ordering_enabled_intl);

  if (settings === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[#7a8a80]">Loading…</div>;
  }

  // FOUNDATION PHASE: online ordering is not yet connected. Never simulate a payment.
  if (!orderingEnabled) {
    return (
      <div data-testid="checkout-page" className="bg-[#FAF7F0] text-[#14241C] min-h-[70vh]">
        <RegionBar />
        <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="ordering-coming-soon">
          <span className="w-16 h-16 rounded-full bg-[#E0A526]/20 text-[#B45B3E] flex items-center justify-center mx-auto mb-6"><Clock size={30} /></span>
          <h1 className="font-display text-3xl font-bold mb-3">Online ordering is coming soon.</h1>
          <p className="text-[#4a5a50] mb-2">
            We're putting the finishing touches on the Refugium Shop. Secure {region.market === "ethiopia" ? "local Ethiopian" : "international"} checkout
            and fulfillment are being set up and will be available here shortly.
          </p>
          <p className="text-sm text-[#7a8a80] mb-8">
            In the meantime you can keep browsing the collection — and support our mission directly through our donation page.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/shop" className="inline-flex items-center gap-2 bg-[#14241C] text-white rounded-full px-7 py-3 font-semibold"><ShoppingBag size={16} /> Continue browsing</Link>
            <Link to="/projects" className="inline-flex items-center gap-2 border border-[#14241C]/20 rounded-full px-7 py-3 font-semibold hover:bg-white">Support the mission <ArrowRight size={16} /></Link>
          </div>
        </div>
      </div>
    );
  }

  // Ordering enabled path — a real payment/fulfillment flow will be connected here in a later phase.
  return (
    <div data-testid="checkout-page" className="bg-[#FAF7F0] text-[#14241C] min-h-[70vh]">
      <RegionBar />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
        <h1 className="font-display text-4xl font-bold mb-2">Checkout</h1>
        <p className="text-sm text-[#B45B3E] font-semibold mb-8">{region.market === "ethiopia" ? "Ethiopia order" : "International order"}</p>
        {cart.length === 0 ? (
          <p className="text-[#7a8a80]" data-testid="checkout-empty">Your cart is empty. <Link to="/shop" className="underline">Browse the shop</Link>.</p>
        ) : (
          <div className="bg-white border border-[#14241C]/10 rounded-2xl p-5 text-sm space-y-2">
            {cart.map((c, i) => (
              <div key={i} className="flex justify-between"><span>{c.name} × {c.qty}</span><span>{fmt(linePrice(c) * c.qty)}</span></div>
            ))}
            <div className="flex justify-between font-bold text-base border-t border-[#14241C]/10 pt-2"><span>Subtotal</span><span data-testid="co-total">{fmt(subtotal)}</span></div>
            <p className="text-xs text-[#7a8a80] pt-2">Payment provider connection required to complete this order.</p>
          </div>
        )}
      </div>
    </div>
  );
}
